import { type ColDef } from "ag-grid-community";
import { AgGrid } from "shared/components/AgGrid";
import { useLayout } from "providers/Layout/hooks";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { SKELETON_ROW_COUNT, skeletonColumns, skeletonRows } from "../columns";

const ROW_HEIGHT = 60;
const HEADER_HEIGHT = 44;
const VIEWPORT_OFFSET = 150;

interface IdentityTheftTableProps<T extends { id?: string | number }> {
  columns: ColDef<T>[];
  rows: T[];
  loading?: boolean;
  footer: React.ReactNode;
}

export function IdentityTheftTable<T extends { id?: string | number }>({
  columns,
  rows,
  loading,
  footer
}: IdentityTheftTableProps<T>) {
  const { sectionHeight } = useLayout();

  const maxRows = Math.floor(
    (sectionHeight - VIEWPORT_OFFSET - HEADER_HEIGHT - 2) / ROW_HEIGHT
  );
  const skeletonCount = Math.max(1, Math.min(SKELETON_ROW_COUNT, maxRows));
  const cappedSkeletonRows = skeletonRows.slice(0, skeletonCount);

  const gridColumns = loading ? skeletonColumns : columns;
  const gridRows = loading ? cappedSkeletonRows : rows;

  return (
    <AgGrid<any>
      columns={gridColumns}
      rows={gridRows}
      rowHeight={ROW_HEIGHT}
      headerHeight={HEADER_HEIGHT}
      perPageRows={skeletonCount}
      viewPortHeight={sectionHeight}
      viewPortHeightOffset={VIEWPORT_OFFSET}
      showFooter
      footer={footer}
      wrapperClassName="[&_.border-t]:!border-t-0 bg-white"
      noRowsFallback={
        <NoRowsFallback
          title="No residents"
          text="There are no residents to display."
        />
      }
      themeOverrides={{
        "--ag-cell-horizontal-padding": "16px"
      }}
    />
  );
}
